import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search, Check, Lock, Plus } from 'lucide-react'
import { useTypeahead } from '../lib/useTypeahead.js'

/**
 * Modern searchable combobox.
 *
 *   <Combobox value={v} onChange={setV} options={[...]} />
 *
 * Props:
 *   options    string[]  — choices
 *   freeText   bool      — allow committing a typed value not in the list
 *                          (each keystroke updates `value`). Without it, only
 *                          options can be chosen (typing just filters).
 *   allowCreate bool     — show an explicit "＋ เพิ่ม “xxx”" row when the query
 *                          matches no option exactly. freeText alone already
 *                          ACCEPTS a new value, but silently: there is nothing
 *                          on screen saying a new one is about to be created,
 *                          and when the query partly matches existing options
 *                          the list looks like those are the only choices.
 *                          The row is part of the keyboard list, not a special
 *                          case bolted beside it.
 *   createLabel func     — (query) => string, overrides the create row's text
 *   disabled   bool
 *   placeholder string
 *   dense      bool      — the compact scale used by the drawer forms (13.5px / py-1.5 /
 *                          rounded-lg) instead of the default form scale (15px / py-2.5 /
 *                          rounded-xl). Purely visual; sitting at the default size next to
 *                          a drawer's own inputs reads as a mis-sized control.
 *
 * Type to filter, click to choose, Esc to close. When closed the input shows the
 * current value; when open it shows the search query (current value as a hint).
 */
const inputCls = (white, dense) =>
  'w-full border border-slate-200/90 pr-9 text-slate-800 ' +
  (dense
    ? 'rounded-lg px-3 py-1.5 text-[13.5px] '
    : 'rounded-xl px-3.5 py-2.5 text-[15px] ') +
  (white ? 'bg-white ' : 'bg-slate-50/60 ') +
  'placeholder:text-slate-400 outline-none transition-all duration-150 ' +
  'hover:border-slate-300 focus:bg-white focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

export default function Combobox({
  value, onChange, options = [], placeholder, disabled, freeText,
  allowCreate, createLabel, white, dense, maxRows = 80,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery('') } }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // Options are strings OR { value, label?, group? }. Normalising here keeps the
  // string form — every existing caller — on exactly the path it had before.
  //   value  what onChange receives and what the closed input shows (must be unique)
  //   label  the row's text; defaults to value. With groups it is the short form,
  //          so "Ortho · ปรึกษาจัดฟัน" reads as "ปรึกษาจัดฟัน" under an Ortho heading
  //          instead of repeating the group on every line.
  //   group  heading this row sits under; '' = ungrouped, rendered flat as before
  const norm = useMemo(() => options.map((o) => (typeof o === 'string'
    ? { value: o, label: o, group: '' }
    : { value: o.value, label: o.label ?? o.value, group: o.group || '' })), [options])
  const grouped = useMemo(() => norm.some((o) => o.group), [norm])

  const q = query.trim().toLowerCase()
  // Search spans the group too, so typing "ortho" finds everything filed under it
  // even though the row text no longer repeats the word.
  const filtered = q
    ? norm.filter((o) => `${o.group} ${o.label} ${o.value}`.toLowerCase().includes(q))
    : norm
  const shown = filtered.slice(0, maxRows)

  // The create row exists only when the typed value is genuinely new. An exact
  // match (case-insensitive) must NOT offer to create a duplicate — that is how
  // "Lab" and "lab" end up as two categories.
  const typed = query.trim()
  const exact = norm.some((o) => o.value.toLowerCase() === typed.toLowerCase())
  const canCreate = !!(allowCreate && typed && !exact)

  // ONE list for both rendering and keyboard nav. Keeping the create row out of
  // this array would make the highlight index and the rendered rows disagree by
  // one, so ArrowDown+Enter would pick the wrong entry.
  //
  // ⚠️ Group HEADINGS are deliberately not in here. They are emitted during the
  // render, between rows, so they never consume a keyboard index — arrowing down
  // must step from treatment to treatment, not stop on "Ortho".
  const rows = canCreate
    ? [{ create: true, value: typed, label: typed, group: '' }, ...shown.map((o) => ({ create: false, ...o }))]
    : shown.map((o) => ({ create: false, ...o }))

  // How many rows sit under each heading, for the count beside it.
  const groupCount = useMemo(() => {
    const m = new Map()
    for (const o of shown) if (o.group) m.set(o.group, (m.get(o.group) || 0) + 1)
    return m
  }, [shown])

  function pick(o) { onChange(o); setOpen(false); setQuery('') }

  // Keyboard navigation over the rendered options.
  //   • non-freeText → autoHighlight lands on shown[0], so Enter picks the top hit
  //     (the legacy behavior) and arrows move from there.
  //   • freeText → NO auto-highlight: Enter commits whatever was typed (the whole
  //     point of freeText). The user can still ArrowDown to pick a real option.
  const ta = useTypeahead({
    count: rows.length,
    autoHighlight: !freeText,
    onSelect: (i) => pick(rows[i].value),   // pick rows[i] just like clicking it (pick() also closes)
    onClose: () => { setOpen(false); setQuery('') },
  })

  function onInput(e) {
    const val = e.target.value
    setQuery(val)
    setOpen(true)
    if (freeText) onChange(val)   // commit typed value live
  }

  function onKeyDown(e) {
    ta.onKeyDown(e)               // Arrow/Home/End move the highlight; Enter picks it; Escape closes (onClose)
    if (e.defaultPrevented) return  // hook consumed the key (incl. a highlighted-option Enter) — don't double-fire
    if (e.key === 'Enter') {
      // Nothing highlighted (empty list): preserve legacy behavior.
      e.preventDefault()
      if (!freeText && rows.length) pick(rows[0].value)
      else { setOpen(false); setQuery('') }   // freeText commits the typed value (already applied live in onInput)
    } else if (e.key === 'Escape') {
      e.currentTarget.blur()      // hook's onClose already closed the menu; restore the legacy blur
    }
  }

  return (
    <div className="relative" ref={ref}>
      <input
        className={`${inputCls(white, dense)} ${disabled ? 'bg-slate-100/70 text-slate-400 cursor-not-allowed' : 'cursor-text'}`}
        disabled={disabled}
        value={open ? query : (value || '')}
        placeholder={value || placeholder}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery('') } }}
        onChange={onInput}
        onKeyDown={onKeyDown}
      />
      {disabled
        ? <Lock size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
        : open
          ? <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          : <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />}

      {open && !disabled && (
        <div ref={ta.listRef} className="absolute z-30 mt-1.5 w-full max-h-60 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 py-1">
          {rows.length === 0 && (
            <div className="px-3 py-2.5 text-[14px] text-slate-400">
              {freeText && q ? `กด Enter เพื่อใช้ “${query.trim()}”` : 'ไม่พบข้อมูล'}
            </div>
          )}
          {rows.map((row, i) => {
            const o = row.value
            const active = i === ta.activeIndex
            if (row.create) {
              return (
                <button
                  key="__create"
                  type="button"
                  data-ti-index={i}
                  onMouseEnter={() => ta.setActiveIndex(i)}
                  onMouseDown={(e) => { e.preventDefault(); pick(o) }}
                  className={`flex w-full items-center gap-2 text-left px-3 font-medium text-brand-700 transition-colors ${
                    dense ? 'py-1.5 text-[13px]' : 'py-2 text-[14.5px]'
                  } ${
                    active ? 'bg-brand-50' : 'hover:bg-brand-50'
                  }`}
                >
                  <Plus size={14} strokeWidth={2.5} className="text-brand-600 shrink-0" />
                  {createLabel ? createLabel(o) : <>เพิ่ม “{o}” เป็นตัวเลือกใหม่</>}
                </button>
              )
            }
            const sel = o === value
            // A heading whenever the group changes. Emitted alongside its first row
            // rather than as its own list entry, so keyboard nav skips straight over it.
            const newGroup = grouped && row.group && row.group !== rows[i - 1]?.group
            return (
              <div key={o} className="contents">
                {newGroup && (
                  <div className={`sticky top-0 z-10 flex items-baseline gap-1.5 bg-white/95 backdrop-blur-sm px-3 ${
                    dense ? 'pt-2 pb-1' : 'pt-2.5 pb-1.5'
                  } ${i === 0 ? '' : 'mt-1 border-t border-slate-100'}`}>
                    <span className="text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">{row.group}</span>
                    <span className="text-[11px] tabular-nums text-slate-300">{groupCount.get(row.group)}</span>
                  </div>
                )}
                <button
                  type="button"
                  data-ti-index={i}
                  onMouseEnter={() => ta.setActiveIndex(i)}
                  onMouseDown={(e) => { e.preventDefault(); pick(o) }}
                  className={`flex w-full items-center gap-2 text-left transition-colors ${
                    grouped ? 'pl-3 pr-3' : 'px-3'
                  } ${
                    dense ? 'py-1.5 text-[13px]' : 'py-2 text-[14.5px]'
                  } ${
                    active ? 'bg-brand-50' : 'hover:bg-brand-50'
                  } ${
                    sel ? `text-brand-700 font-medium${active ? '' : ' bg-brand-50/60'}` : 'text-slate-700'
                  }`}
                >
                  <Check size={13} strokeWidth={3} className={`shrink-0 ${sel ? 'text-brand-600' : 'text-transparent'}`} />
                  {/* truncate only in grouped mode. The ungrouped callers (addresses,
                      seller names) rely on long options WRAPPING, and an ellipsis there
                      would hide the part that tells two entries apart. */}
                  <span className={grouped ? 'truncate' : ''}>{row.label}</span>
                </button>
              </div>
            )
          })}
          {filtered.length > shown.length && (
            <div className="px-3 py-1.5 text-[12.5px] text-slate-400">พิมพ์เพื่อค้นหาเพิ่มเติม… (+{filtered.length - shown.length})</div>
          )}
        </div>
      )}
    </div>
  )
}
